import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Flex,
  Input,
  Tabs,
  Title,
  TextInput,
  Box,
  Text,
} from '@mantine/core';
import toast from 'react-hot-toast';
import InputMask from 'react-input-mask';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { UserLockModal } from '@components/User';
import { ResendLink, SignupFlow } from './UserAuth';
import { CodeInput } from '@components/Auth';

const defaultFormState = {
  email: '',
  password: '',
};

export default function UserLoginForm({ locationId }) {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(defaultFormState);
  const [phoneValue, setPhoneValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [codeData, setCodeData] = useState(null);
  const [codeValue, setCodeValue] = useState('');
  const [activeTab, setActiveTab] = useState('phone');
  const [lockData, setLockData] = useState(null);

  useEffect(() => {
    setLoading(false);
    setCodeData(null);
    setCodeValue('');
    setPhoneValue('');
    setFormValues(defaultFormState);
  }, [activeTab]);

  useEffect(() => {
    if (codeValue.length === 6) {
      verifyCode();
    }
  }, [codeValue]);

  function onSubmit(e) {
    e.preventDefault();
    setLoading(true);

    const req = {
      location_id: locationId,
      email: formValues.email,
      password: formValues.password,
    };

    axios
      .post(`/user-login/`, req)
      .then(({ data }) => {
        const res = data.response[0];

        if (res.modal) {
          return setLockData(res);
        }

        if (res.need_email_verified) {
          setScenario(res);
        } else if (res.migration) {
          setScenario(res);
        } else {
          localStorage.setItem('mixer-entry-point', window.location.pathname);
          window.location = `/`;
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onPhoneSubmit() {
    const req = {
      location_id: locationId,
      mobile_phone: phoneValue,
    };

    setLoading(true);

    axios
      .post(`/send-otp-code/`, req)
      .then(({ data }) => {
        setCodeData(data.response[0]);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function verifyCode() {
    const req = {
      code: codeValue,
      phone_id: codeData.phone_id,
      user_location_id: codeData.user_location_id,
    };

    axios
      .post(`/verify-text-code/`, req)
      .then(({ data }) => {
        const res = data.response[0];

        if (res.modal) {
          return setLockData(res);
        }

        if (res.need_email_verified) {
          setScenario(res);
        } else if (res.migration) {
          setScenario(res);
        } else {
          localStorage.setItem('mixer-entry-point', window.location.pathname);
          window.location = `/`;
        }
      })
      .catch((err) => {
        setCodeValue('');
        toast.error(err);
      });
  }

  if (scenario) {
    if (scenario.need_signup_form || scenario.migration) {
      return (
        <SignupFlow
          email={formValues.email || scenario.email}
          firstName={scenario.first_name}
          lastName={scenario.last_name}
          locationId={locationId}
          locationName={scenario.location_name}
          migrationScenario={scenario}
          mobilePhone={scenario.mobile_phone || phoneValue.replace(/\D/g, '')}
          zip={scenario.zip}
        />
      );
    }
    if (scenario.need_email_verified) {
      return (
        <ResendLink
          reqData={{
            user_location_id: scenario.user_location_id,
          }}
        />
      );
    }
  }

  return (
    <div>
      {/* <UserLockModal data={lockData} /> */}
      {/* <Title mb="lg" align="center" order={2}>
        Login
      </Title> */}
      <Flex justify="center" gap="sm" mt="xl" mb="lg">
        <Button
          variant={activeTab === 'phone' ? 'filled' : 'light'}
          radius="xl"
          size="sm"
          onClick={() => setActiveTab('phone')}
        >
          {t('user_app:login_with_phone')}
        </Button>
        <Button
          variant={activeTab === 'email' ? 'filled' : 'light'}
          radius="xl"
          size="sm"
          onClick={() => setActiveTab('email')}
        >
          {t('user_app:login_with_email')}
        </Button>
      </Flex>
      <Box>
        {activeTab === 'phone' && (
          <React.Fragment>
            {codeData ? (
              <React.Fragment>
                {codeData.opted_out ? (
                  <Box mt="lg">
                    <Text align="center" mb="lg">
                      {phoneValue} has opted out of receiving text messages.
                      Text START to <b>{codeData.opt_in_number}</b> to opt-in.
                    </Text>
                    <Flex justify="center">
                      <Button onClick={() => setCodeData(null)}>
                        Try again
                      </Button>
                    </Flex>
                  </Box>
                ) : (
                  <>
                    <StyledInputs>
                      <Title order={4} align="center" mb="sm">
                        Enter code
                      </Title>
                      <CodeInput
                        value={codeValue}
                        onChange={(e) => setCodeValue(e)}
                      />
                    </StyledInputs>
                    <Flex justify="center" mt="xl">
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                          setCodeData(null);
                        }}
                      >
                        Didn't receive code?
                      </Button>
                    </Flex>
                  </>
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Input.Wrapper>
                  <Input
                    size="lg"
                    component={InputMask}
                    placeholder="(XXX) XXX-XXXX"
                    mask="(999) 999-9999"
                    styles={{
                      input: {
                        textAlign: 'center',
                      },
                    }}
                    onChange={(e) => {
                      setPhoneValue(e.target.value);
                    }}
                    value={phoneValue}
                  />
                </Input.Wrapper>
                <Button
                  disabled={phoneValue.length !== 14}
                  fullWidth
                  mt="md"
                  size="md"
                  type="submit"
                  onClick={onPhoneSubmit}
                  loading={loading}
                >
                  {t('user_app:get_code')}
                </Button>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {activeTab === 'email' && (
          <div>
            <form onSubmit={onSubmit}>
              <TextInput
                mb="xs"
                size="md"
                placeholder={t('common:email')}
                enterKeyHint="next"
                value={formValues.email}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    email: e.target.value,
                  })
                }
              />
              <TextInput
                enterKeyHint="done"
                size="md"
                type="password"
                placeholder={t('common:password')}
                value={formValues.password}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    password: e.target.value,
                  })
                }
              />
              <Button
                disabled={!formValues.email || !formValues.password}
                fullWidth
                mt="md"
                size="md"
                type="submit"
                onClick={onSubmit}
                loading={loading}
              >
                {t('common:submit')}
              </Button>
            </form>
          </div>
        )}
      </Box>
    </div>
  );
}

const StyledInputs = styled.div`
  display: block;
  /* input {
    height: 70px;
    width: 50px;
  } */
`;
